/* =========================================================================== *\
 __      __.__     ___________           .__    ____ ________  ________.________
/  \    /  \__| ___\__    ___/___   ____ |  |__/_   /   __   \/   __   \   ____/
\   \/\/   /  |/    \|    |_/ __ \_/ ___\|  |  \|   \____    /\____    /____  \ 
 \        /|  |   |  \    |\  ___/\  \___|   Y  \   |  /    /    /    //       \
  \__/\  / |__|___|  /____| \___  >\___  >___|  /___| /____/    /____//______  /
       \/          \/           \/     \/     \/                             \/ 
\* =========================================================================== */

import React, { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';

// @mui material components
import Card from '@mui/material/Card';
import Icon from '@mui/material/Icon';
import Menu from '@mui/material/Menu';
import MenuItem from '@mui/material/MenuItem';
import Backdrop from '@mui/material/Backdrop';
import CircularProgress from '@mui/material/CircularProgress';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableContainer from '@mui/material/TableContainer';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import Paper from '@mui/material/Paper';
import Pagination from '@mui/material/Pagination';
import Grid from '@mui/material/Grid';
import InputAdornment from '@mui/material/InputAdornment';
import Avatar from '@mui/material/Avatar';

// Material Dashboard 2 PRO React components
import MDBox from 'components/MDBox';
import MDButton from 'components/MDButton';
import MDInput from 'components/MDInput';

// Material Dashboard 2 PRO React examples
import Navbar from 'examples/Navbars/NavBar';
import { APP_STATE } from 'services/Define';
import { FetchData } from 'services/Request';
import { useSelector } from 'react-redux';
import * as Func from 'services/Function';

const initFind = {
  search: '',
  page: 1,
  perPage: 10,
  pageTotal: 0,
  checkpoint: 'ALL',
  best: 'ALL',
  branch: 'ALL',
  sort: 'diffstock',
  condition: 'branch_stock',
};

function RpPurchaseBranch() {
  const mod = 'rp_purchase_branch';
  const app = useSelector((state) => state[APP_STATE]);
  const { t, i18n } = useTranslation();
  const { id } = useParams();
  const [pageTotal, setPageTotal] = useState(0);
  const [pageNo, setPageNo] = useState(1);
  const [record, setRecord] = useState([]);
  const [show, setShow] = useState(null);
  const [find, setFind] = useState(initFind);
  const [loading, setLoading] = useState(true);
  const [cboBranch, setCboBranch] = useState([]);
  const [cboBest, setCboBest] = useState([]);
  const [urlExcel, setUrlExcel] = useState('');

  const openShow = (e) => setShow(e.currentTarget);
  const closeShow = () => setShow(null);

  const view = (data) => {
    setFind(data);
    setLoading(true);
    closeShow();

    FetchData('POST', `/${mod}`, app.token, {
      lang: app.lang,
      search: data.search,
      page: data.page,
      per_page: data.perPage,
      orderby: data.order_by,
      ordersort: data.order_sort,
      branch: data.branch,
      best: data.best,
      checkpoint: data.checkpoint,
      sort: data.sort,
      condition: data.condition,
    }).then((result) => {
      if (result.status === 'ok') {
        setRecord(result.data);

        setPageTotal(result.total_pages);
        setPageNo(result.page);
      }
      setLoading(false);
    });
  };

  const excel = () => {
    const params = new URLSearchParams();
    params.append('search', find.search);
    params.append('branch', find.branch);
    params.append('best', find.best);
    params.append('condition', find.condition);

    const url = `${urlExcel}/download?${params.toString()}`;
    window.open(url, '_self');
  };

  const imageDisplay = (item) => {
    return <Avatar alt={item.name} src={item.image} variant='rounded' />;
  };

  const renderLoading = () => {
    return (
      <Backdrop sx={{ color: '#fff', zIndex: (theme) => theme.zIndex.drawer + 1 }} open={loading}>
        <CircularProgress color='inherit' />
      </Backdrop>
    );
  };

  const renderShow = (
    <Menu
      anchorEl={show}
      anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
      transformOrigin={{ vertical: 'top', horizontal: 'left' }}
      open={Boolean(show)}
      onClose={closeShow}
      keepMounted
    >
      <MenuItem onClick={() => view({ ...find, page: 1, perPage: 10 })}>
        {t(`element.show_record`, { num: 10 })}
      </MenuItem>
      <MenuItem onClick={() => view({ ...find, page: 1, perPage: 20 })}>
        {t(`element.show_record`, { num: 20 })}
      </MenuItem>
      <MenuItem onClick={() => view({ ...find, page: 1, perPage: 100 })}>
        {t(`element.show_record`, { num: 100 })}
      </MenuItem>
    </Menu>
  );

  const init = () => {
    FetchData('GET', `/${mod}`, app.token, {
      lang: app.lang,
    }).then((result) => {
      if (result.status === 'ok') {
        setCboBranch(result.branch);
        setCboBest(result.best);
        setUrlExcel(result.excel);
        view(find);
      } else {
        Func.exitApp();
      }
    });
  };

  const renderSearch = () => {
    return (
      <Card sx={{ p: 2 }}>
        <Grid container spacing={2}>
          <Grid item xs={12} sm={6} md={6} lg={3}>
            <MDBox
              noValidate
              component='form'
              role='form'
              onSubmit={(e) => {
                e.preventDefault();
                view({ ...find, period: 'ALL', page: 1 });
              }}
            >
              <MDInput
                name='searchform'
                fullWidth
                value={find.search}
                type='search'
                onChange={(e) => setFind({ ...find, search: e.target.value, page: 1 })}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position='start'>{t('element.search')} :</InputAdornment>
                  ),
                  autoComplete: 'off',
                }}
              />
            </MDBox>
          </Grid>
          <Grid item xs={12} sm={6} md={6} lg={3}>
            <MDInput
              fullWidth
              value={find.branch}
              defaultValue='ALL'
              select
              onChange={(e) => view({ ...find, branch: e.target.value, page: 1 })}
              InputProps={{
                startAdornment: (
                  <InputAdornment position='start'>
                    <span>สาขา :</span>
                  </InputAdornment>
                ),
              }}
            >
              <MenuItem value='ALL'>ทั้งหมด</MenuItem>
              {cboBranch.map((item) => (
                <MenuItem key={item.id} value={item.id}>
                  {item.name}
                </MenuItem>
              ))}
            </MDInput>
          </Grid>
          <Grid item xs={12} sm={6} md={6} lg={2}>
            <MDInput
              fullWidth
              value={find.best}
              defaultValue='ALL'
              select
              onChange={(e) => view({ ...find, best: e.target.value, page: 1 })}
              InputProps={{
                startAdornment: (
                  <InputAdornment position='start'>
                    <span>Rating :</span>
                  </InputAdornment>
                ),
              }}
            >
              <MenuItem value='ALL'>ทั้งหมด</MenuItem>
              {cboBest.map((item) => (
                <MenuItem key={item.id} value={item.id}>
                  {item.name}
                </MenuItem>
              ))}
            </MDInput>
          </Grid>
          <Grid item xs={12} sm={6} md={6} lg={3}>
            <MDInput
              fullWidth
              value={find.condition}
              defaultValue='ALL'
              select
              onChange={(e) => view({ ...find, condition: e.target.value, page: 1 })}
              InputProps={{
                startAdornment: (
                  <InputAdornment position='start'>
                    <span>เงื่อนไข :</span>
                  </InputAdornment>
                ),
              }}
            >
              <MenuItem value='ALL'>ทั้งหมด</MenuItem>
              <MenuItem value='branch'>หน้าร้านไม่เพียงพอ</MenuItem>
              <MenuItem value='stock'>STO ไม่เพียงพอ</MenuItem>
              <MenuItem value='branch_stock'>ถึงจุดสั่งซื้อ และโอนย้าย</MenuItem>
            </MDInput>
          </Grid>
          <Grid item xs={12} sm={6} md={6} lg={1}>
            <MDBox display='flex' justifyContent='end' gap={1} onClick={excel}>
              <MDButton variant='gradient' color='success'>
                <Icon>table_view</Icon>&nbsp;Excel
              </MDButton>
            </MDBox>
          </Grid>
        </Grid>
      </Card>
    );
  };

  const balanceStock = (row) => {
    if (row.balance_stock > 0) {
      return <MDBox color='error'>{Func.numberDisplay(row.balance_stock)}</MDBox>;
    }

    return '-';
  };

  const balanceBranch = (row) => {
    if (row.balance_branch > 0) {
      return <MDBox color='error'>{Func.numberDisplay(row.balance_branch)}</MDBox>;
    }

    return '-';
  };

  const balanceAll = (row) => {
    if (row.balance_all > 0) {
      return <MDBox color='primary'>{Func.numberDisplay(row.balance_all)}</MDBox>;
    }

    return '-';
  };

  const diffStockDisplay = (row) => {
    const diff = row.diff_stock * 1;

    if (diff <= 0) {
      return <MDBox color='error'>{Func.numberDisplay(row.purchase_stock)}</MDBox>;
    }

    return <MDBox>{Func.numberDisplay(row.purchase_stock)}</MDBox>;
  };

  const diffBranchDisplay = (row) => {
    const diff = row.diff_branch * 1;

    if (diff <= 0) {
      return <MDBox color='error'>{Func.numberDisplay(row.purchase_branch)}</MDBox>;
    }

    return <MDBox>{Func.numberDisplay(row.purchase_branch)}</MDBox>;
  };

  const quantityDisplay = (inp) => {
    if (inp * 1 < 0) {
      return <MDBox color='error'>{Func.numberDisplay(inp)}</MDBox>;
    }

    return <MDBox>{Func.numberDisplay(inp)}</MDBox>;
  };

  const renderList = () => {
    return (
      <MDBox mb={7}>
        <Grid container spacing={2}>
          <Grid item xs={12} sm={3} md={2} lg={1}>
            <MDButton
              variant='contained'
              color='dark'
              size='large'
              fullWidth
              sx={{ pt: 1.6, pb: 1.6 }}
              href='/report_purchase'
            >
              <Icon>content_paste_search</Icon>
            </MDButton>
          </Grid>
          <Grid item xs={12} sm={9} md={10} lg={11}>
            {renderSearch()}
          </Grid>
          <Grid item xs={12}>
            <TableContainer component={Paper} sx={{ minHeight: 400 }}>
              <Table aria-label='datatable table'>
                <TableHead>
                  <TableRow>
                    <TableCell className='nowrap' align='center' rowSpan={2}>
                      No.
                    </TableCell>
                    <TableCell className='nowrap' rowSpan={2}>
                      รูป
                    </TableCell>
                    <TableCell className='nowrap' rowSpan={2}>
                      สินค้า
                    </TableCell>
                    <TableCell className='nowrap' rowSpan={2}>
                      สาขา
                    </TableCell>
                    <TableCell className='nowrap' rowSpan={2}>
                      ผู้จัดจำหน่าย
                    </TableCell>
                    <TableCell className='nowrap' align='center' rowSpan={2}>
                      Rating
                    </TableCell>
                    <TableCell className='nowrap' align='center' rowSpan={2}>
                      Min / Max
                    </TableCell>
                    <TableCell className='nowrap' align='right' rowSpan={2}>
                      ขายเฉลี่ย
                      <br />
                      ต่อวัน
                    </TableCell>
                    <TableCell className='nowrap highlight-warning' align='center' colSpan={3}>
                      หน้าร้าน
                    </TableCell>
                    <TableCell className='nowrap highlight-info' align='center' colSpan={4}>
                      STO
                    </TableCell>
                    <TableCell className='nowrap highlight-success' align='center' colSpan={5}>
                      สั่งซื้อ
                    </TableCell>
                  </TableRow>
                  <TableRow>
                    <TableCell className='nowrap highlight-warning' align='right'>
                      จุดเติม
                    </TableCell>
                    <TableCell className='nowrap highlight-warning' align='right'>
                      คลัง
                    </TableCell>
                    <TableCell className='nowrap highlight-warning' align='right'>
                      คงเหลือ
                    </TableCell>
                    <TableCell className='nowrap highlight-info' align='right'>
                      จุดสั่ง
                    </TableCell>
                    <TableCell className='nowrap highlight-info' align='right'>
                      เติมเต็ม
                    </TableCell>
                    <TableCell className='nowrap highlight-info' align='right'>
                      คลัง
                    </TableCell>
                    <TableCell className='nowrap highlight-info' align='right'>
                      คงเหลือ
                    </TableCell>
                    <TableCell className='nowrap highlight-success' align='right'>
                      ALL
                    </TableCell>
                    <TableCell className='nowrap highlight-success' align='right'>
                      หน้าร้าน
                    </TableCell>
                    <TableCell className='nowrap highlight-success' align='right'>
                      STO
                    </TableCell>
                    <TableCell className='nowrap highlight-success' align='right'>
                      สุทธิ
                    </TableCell>
                    <TableCell className='nowrap highlight-success' align='right'>
                      ซื้อแล้ว
                    </TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {record.map((row) => (
                    <TableRow key={row.id} hover>
                      <TableCell align='center'>{row.id}</TableCell>
                      <TableCell width='40'>{imageDisplay(row)}</TableCell>
                      <TableCell>
                        <MDBox>{row.code}</MDBox>
                        <MDBox className='cut' sx={{ width: 150 }}>
                          {row.product_name}
                        </MDBox>
                      </TableCell>
                      <TableCell>
                        <MDBox>{row.branch_code}</MDBox>
                        <MDBox>{row.delivery_cycle} วัน</MDBox>
                      </TableCell>
                      <TableCell>
                        <MDBox className='nowrap'>{row.supplier_code}</MDBox>
                        <MDBox className='nowrap'>
                          {Func.numberDisplay(row.lead_time)} +{' '}
                          {Func.numberDisplay(row.review_period)}
                        </MDBox>
                      </TableCell>
                      <TableCell align='center'>
                        {row.best_name === '' ? (
                          'N/A'
                        ) : (
                          <MDBox>
                            <MDBox>
                              <code>{row.best_name}</code>
                            </MDBox>
                            <MDBox>{Func.numberDisplay(row.percent_request)}%</MDBox>
                          </MDBox>
                        )}
                      </TableCell>
                      <TableCell align='right'>
                        <MDBox className='nowrap'>
                          {Func.numberDisplay(row.quantity_min)} /{' '}
                          {Func.numberDisplay(row.quantity_max)}
                        </MDBox>
                        <MDBox>{row.unit_name}</MDBox>
                      </TableCell>
                      <TableCell align='right'>
                        <MDBox>{Func.numberDisplay(row.quantity_avg)}</MDBox>
                        <MDBox>{row.unit_name}</MDBox>
                      </TableCell>
                      <TableCell align='right'>
                        <MDBox color='info'>{Func.numberDisplay(row.delivery_point)}</MDBox>
                        <MDBox>วัน</MDBox>
                      </TableCell>
                      <TableCell align='right'>
                        <MDBox>{quantityDisplay(row.quantity_balance)}</MDBox>
                        <MDBox>{row.unit_name}</MDBox>
                      </TableCell>
                      <TableCell align='right' className='highlight-warning'>
                        <MDBox>{diffBranchDisplay(row)}</MDBox>
                        <MDBox>วัน</MDBox>
                      </TableCell>
                      <TableCell align='right'>
                        <MDBox color='info'>{Func.numberDisplay(row.day_purchase)}</MDBox>
                        <MDBox>วัน</MDBox>
                      </TableCell>
                      <TableCell align='right'>
                        <MDBox color='info'>{Func.numberDisplay(row.purchase_standard)}</MDBox>
                        <MDBox>วัน</MDBox>
                      </TableCell>
                      <TableCell align='right'>
                        <MDBox>{Func.numberDisplay(row.quantity_stock)}</MDBox>
                        <MDBox className='nowrap'>{row.unit_name}</MDBox>
                      </TableCell>
                      <TableCell align='right' className='highlight-info'>
                        <MDBox>{diffStockDisplay(row)}</MDBox>
                        <MDBox>วัน</MDBox>
                      </TableCell>
                      <TableCell align='right'>
                        <MDBox className='color-orange'>
                          {Func.numberDisplay(row.quantity_point)}
                        </MDBox>
                        <MDBox>{row.unit_name}</MDBox>
                      </TableCell>
                      <TableCell align='right'>
                        <MDBox>{balanceBranch(row)}</MDBox>
                        <MDBox>{row.unit_name}</MDBox>
                      </TableCell>
                      <TableCell align='right'>
                        <MDBox>{balanceStock(row)}</MDBox>
                        <MDBox>{row.unit_name}</MDBox>
                      </TableCell>
                      <TableCell align='right' className='highlight-danger'>
                        <MDBox>{balanceAll(row)}</MDBox>
                        <MDBox>{row.unit_name}</MDBox>
                      </TableCell>
                      <TableCell align='right' className='highlight-success'>
                        <MDBox>{Func.numberDisplay(row.quantity_purchase)}</MDBox>
                        <MDBox>{row.unit_name}</MDBox>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          </Grid>
          <Grid item xs={12}>
            <Card sx={{ p: 2 }}>
              <MDBox display='flex' justifyContent='space-between' alignItems='flex-start'>
                <MDButton variant={show ? 'contained' : 'outlined'} color='dark' onClick={openShow}>
                  {t(`element.show_record`, { num: find.perPage })}&nbsp;
                  <Icon>keyboard_arrow_down</Icon>
                </MDButton>
                {renderShow}
                <Pagination
                  count={pageTotal}
                  variant='outlined'
                  color='secondary'
                  showFirstButton
                  showLastButton
                  page={pageNo}
                  onChange={(e, p) => view({ ...find, page: p })}
                />
              </MDBox>
            </Card>
          </Grid>
        </Grid>
      </MDBox>
    );
  };

  useEffect(() => {
    i18n.changeLanguage(app.lang);

    init();
  }, [id]);

  return (
    <MDBox>
      <Navbar route={[t(`menu.report_purchase`), t(`menu.rp_purchase_branch`)]} />
      {renderList()}
      {renderLoading()}
    </MDBox>
  );
}

export default RpPurchaseBranch;
